import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductName } from './../app/shared/constants/product-domain';
import { AppService } from './../app/shared/state/app/app.service';
import { Router, NavigationEnd, UrlTree, UrlSegment, PRIMARY_OUTLET } from '@angular/router';
import { AfterViewInit, Component, Renderer2, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { OnInit, OnDestroy } from '@angular/core';
import { Subject, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { transition, trigger, query, style, animate } from '@angular/animations';
import { TrackingService } from '../app/shared/services/tracking/tracking.service';
import { QuestionnaireService } from '../app/shared/services/questionnaire/questionnaire.service';
import { WindowRef } from '../app/shared/services/windowRef.service';
import { BackButtonService } from '../app/shared/services/back-button.service';
import { TealiumTagService, TealiumUtagService } from '../app/shared/services/tracking/tealium-tag.service';
import { SEADService } from '../app/shared/services/tracking/sead.service';
import { ExperimentsService } from '../app/shared/services/experiments/experiments.service';
import { EventService } from '../app/shared/services/event.service';
import { QuestionnaireRoutingService } from '../app/shared/services/questionnaire-routing/questionnaire-routing.service';
import { QueueService, IQueueEntry, TrackJsErrorLogService } from '@legalzoom/business-formation-sdk';
import { HttpErrorResponse } from '@angular/common/http';
import { QuestionnaireStorageService } from '@legalzoom/questionnaire-storage-sdk';
import { ThemeService } from '../app/shared/services/theme.service';
import { NotificationService } from '../app/pl-features/glo-design/shared/services/notification.service';
import { NotificationContainerComponent } from '../app/pl-features/glo-design/shared/components/notification-container/notification-container.component';
import { Chapter, PagePath } from '../app/shared/models/page-model';
import { EnvironmentService } from '../app/shared/services/environment.service';

@Component({
  selector: 'app-root',
  templateUrl: './app-desktop.component.html',
  styleUrls: ['./app-desktop.component.css'],
  animations: [
    trigger('animRoutes', [
      transition('* <=> *', [
        // Initial state of new route
        query(
          ':enter',
          style({
            position: 'absolute',
            top: '60px',
            opacity: 0
          }),
          { optional: true }
        ),
        // Initial state of old route
        query(
          ':leave',
          style({
            position: 'absolute',
            top: 0,
            transform: 'translateY(0px)',
            opacity: 1
          }),
          { optional: true }
        ),

        query(
          ':leave',
          animate(
            '300ms linear',
            style({
              position: 'absolute',
              transform: 'translateY(-60px)',
              opacity: 0
            })
          ),
          { optional: true }
        ),

        query(
          ':enter',
          animate(
            '300ms linear',
            style({
              opacity: 1,
              transform: 'translateX(0%)',
              top: 0
            })
          ),
          { optional: true }
        )
      ])
    ])
  ]
})
export class AppDesktopComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(NotificationContainerComponent, { static: false }) public notificationContainer: NotificationContainerComponent;

  private unsubscribe: Subject<void> = new Subject();
  public outletActive = false;
  private currentPagePath: string;
  public isGloDesignTest: boolean;
  public hidePrev = false;
  public showClassicHeader = false;

  public constructor(
    public winRef: WindowRef,
    private titleService: Title,
    public questionnaireService: QuestionnaireService,
    public tracking: TrackingService,
    public tealium: TealiumTagService,
    public tealiumService: TealiumUtagService,
    public renderer2: Renderer2,
    private router: Router,
    private sead: SEADService,
    private backButtonService: BackButtonService,
    private experimentService: ExperimentsService,
    private eventService: EventService,
    private questionnaireRoutingService: QuestionnaireRoutingService,
    private queueService: QueueService,
    public appService: AppService,
    private questionnaireStorageService: QuestionnaireStorageService,
    private trackJsErrorLogService: TrackJsErrorLogService,
    public themeService: ThemeService,
    public notificationService: NotificationService,
    private environmentService: EnvironmentService,
    private modalService: NgbModal 
  ) {}
  ngOnInit() {
    this.titleService.setTitle('Creating your LLC');
    this.showClassicHeader = true;
    this.isGloDesignTest = this.experimentService.getGloDesignTest();

    this.tealium.setConfig(this.renderer2);
    this.tealium.setTag(this.renderer2);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const url = this.router.url;
        // save last visited page in case user decides to return later
        // this.questionnaireService.llc.currentView = url;
        this.currentPagePath = url;
        // we're going to make the assumption that not too many people will navigate back in the flow
        // nativeWin.pageNumber++;
        this.tracking.SetCurrentPageToTrack(url);
        window.scrollTo(0, 0);

        window.dispatchEvent(new CustomEvent('lz_hashchange'));
        // keep alignment and avoid adding the 'container' class to each page
        const belowFoldElement = document.querySelector('.below-fold');
        if (belowFoldElement) {
          belowFoldElement.classList.add('container');
        }
        this.sead.DetectFields();

        // check test for down the flow targeting
        this.questionnaireService.llc.bShowPriceTest = this.experimentService.GetBShowPriceTestValue();
        this.questionnaireService.llc.bShowPriceTest1 = this.experimentService.GetBShowPriceTest1Value();
        this.modalService.dismissAll();
      }
    });

    this.backButtonService.init();

    this.eventService.saveAndContinueEvent.pipe(takeUntil(this.unsubscribe)).subscribe((pathPath: string) => {
      // get next page
      const nextPage = this.questionnaireRoutingService.getNextPage(pathPath);
      this.questionnaireService.llc.currentView = nextPage;
      this.appService.currentView = nextPage;
      // save questionnaire storage data
      this.queueService.add(this.prepareUpdateQuestionnaireStorage());
      const process = this.queueService.process();
      process.subscribe();
      // navigate to next page
      this.router.navigate([`./${nextPage}`], {
        queryParams: {
          uo: this.appService.processingOrderId,
          cartId: this.appService.cartId,
          orderId: this.appService.orderId
        },
        queryParamsHandling: 'merge'
      });
    });

    // TODO: turn this into a service as it's being used in multiple places
    this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }

      if (this.router.url.startsWith('/' + Chapter.Checkout + '/' + PagePath.ReviewYourOrder) || this.router.url.startsWith('/' + Chapter.Checkout + '/' + PagePath.Checkout) || this.router.url.startsWith('/' + Chapter.PostOrder + '/')) {
        this.hidePrev = true;
      } else {
        this.hidePrev = false;
      }
    });
  }

  prevBtn(): void {
    this.questionnaireService.broswerHistory = this.questionnaireService.broswerHistory || [];

    this.questionnaireService.broswerHistory.push(this.router.url);

    const urlTree: UrlTree = this.router.parseUrl(this.router.url);

    if (urlTree.root.hasChildren) {
      const urlSegments: UrlSegment[] = urlTree.root.children[PRIMARY_OUTLET].segments;
      const lastSegment: string = urlSegments[urlSegments.length - 1].path;

      // the business state page is the app entry point
      // so redirect to the LLC overview page if the current route is the app entry point
      if (lastSegment === PagePath.BusinessState) {
        this.redirectToLLCLandingPage();
        return;
      }

      // otherwise, redirect to the previous app route
      const prevPage = this.questionnaireRoutingService.getPreviousPage(lastSegment);
      this.router
        .navigate([prevPage], {
          queryParams: {
            uo: this.appService.processingOrderId,
            cartId: this.appService.cartId,
            orderId: this.appService.orderId
          },
          queryParamsHandling: 'merge'
        })
        .then(() => {
          // save last visited page
          this.questionnaireService.llc.currentView = prevPage;
          this.appService.currentView = prevPage;
          this.questionnaireStorageService.updateQuestionnaireStorage(this.appService.app.customerId, this.appService.app.processingOrderId, this.questionnaireService.llc).subscribe();
        });
      return;
    }

    // the current route should have children, but just in case, default to LLC landing page
    this.redirectToLLCLandingPage();
    return;
  }

  redirectToLLCLandingPage = () => (this.winRef.nativeWindow.location.href = PagePath[this.environmentService.useGloTheme() ? 'LLCLzrLandingPage' : 'LLCLandingPage']);

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.notificationService.setNotificationContainer(this.notificationContainer);
    });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  getPageState(outlet) {
    return this.outletActive ? outlet.activatedRoute : false;
  }

  public prepareUpdateQuestionnaireStorage(): IQueueEntry {
    const that = this;
    return {
      name: 'prepareUpdateQuestionnaireStorage ' + this.constructor.name,
      pre: () => {
        return that.questionnaireStorageService.updateQuestionnaireStorage(that.appService.app.customerId, that.appService.app.processingOrderId, that.questionnaireService.llc);
      },
      post: (response) => {},
      error: (error: HttpErrorResponse) => {
        console.log(error.message);
        this.trackJsErrorLogService.track(ProductName.LLC, this.constructor.name, error);
        return of(null);
      }
    };
  }
}
